import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Stack, TextField, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { Partner, PartnerRelationshipType } from '../../api/types';
import { PARTNER_RELATIONSHIP_TYPES } from '../../api/types';
import { useDeletePartner, useSavePartner } from '../../api/hooks';
import { ApiError } from '../../api/client';
import { blankToNull } from '../common/format';
import { ConfirmDialog, useErrorMessage } from '../common/Common';

export function PartnerFormDialog({
  open, partner, onClose, onSaved,
}: {
  open: boolean;
  partner?: Partner | null;
  onClose: () => void;
  onSaved?: (partner: Partner) => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const describeError = useErrorMessage();

  const save = useSavePartner();
  const remove = useDeletePartner();

  const [name, setName] = useState('');
  const [relationshipType, setRelationshipType] = useState<PartnerRelationshipType>('reseller');
  const [margin, setMargin] = useState('');
  const [website, setWebsite] = useState('');
  const [notes, setNotes] = useState('');
  const [touched, setTouched] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(partner?.name ?? '');
    setRelationshipType(partner?.defaultRelationshipType ?? 'reseller');
    setMargin(partner?.defaultMarginPercent != null ? String(partner.defaultMarginPercent) : '');
    setWebsite(partner?.website ?? '');
    setNotes(partner?.notes ?? '');
    setTouched(false);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, partner]);

  const nameMissing = name.trim().length === 0;
  const marginValue = margin.trim() === '' ? null : Number(margin);
  const marginInvalid = marginValue !== null
    && (Number.isNaN(marginValue) || marginValue < 0 || marginValue > 100);
  const serverErrors = save.error instanceof ApiError ? save.error.fieldErrors : {};
  const conflict = save.error instanceof ApiError && save.error.code === 'conflict';

  const submit = async () => {
    setTouched(true);
    if (nameMissing || marginInvalid) return;
    const saved = await save.mutateAsync({
      id: partner?.id,
      body: {
        name: name.trim(),
        defaultRelationshipType: relationshipType,
        defaultMarginPercent: marginValue,
        website: blankToNull(website),
        notes: blankToNull(notes),
      },
    });
    onSaved?.(saved);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ flexGrow: 1 }}>{partner ? t('partner.editPartner') : t('partner.newPartner')}</span>
          {partner && (
            <Tooltip title={t('common.delete')}>
              <IconButton
                size="small"
                color="error"
                onClick={() => setConfirmingDelete(true)}
                data-testid="delete-partner"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {conflict && <Alert severity="warning">{describeError(save.error)}</Alert>}
            {save.error && !conflict && !Object.keys(serverErrors).length && (
              <Alert severity="error">{describeError(save.error)}</Alert>
            )}

            <TextField
              label={t('common.name')}
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setTouched(true)}
              error={(touched && nameMissing) || Boolean(serverErrors.name)}
              helperText={touched && nameMissing ? t('partner.nameRequired') : serverErrors.name}
              inputProps={{ dir: 'auto', maxLength: 200, 'data-testid': 'partner-name' }}
            />

            <TextField
              select
              required
              label={t('partner.relationshipType')}
              value={relationshipType}
              onChange={(e) => setRelationshipType(e.target.value as PartnerRelationshipType)}
              data-testid="partner-type"
            >
              {PARTNER_RELATIONSHIP_TYPES.map((type) => (
                <MenuItem key={type} value={type}>{t(`partner.relationship.${type}`)}</MenuItem>
              ))}
            </TextField>

            <TextField
              label={t('partner.defaultMargin')}
              type="number"
              value={margin}
              onChange={(e) => setMargin(e.target.value)}
              error={(touched && marginInvalid) || Boolean(serverErrors.defaultMarginPercent)}
              helperText={
                touched && marginInvalid
                  ? t('partner.marginRange')
                  : serverErrors.defaultMarginPercent ?? t('partner.marginHint')
              }
              inputProps={{ min: 0, max: 100, step: '0.01', 'data-testid': 'partner-margin' }}
              InputProps={{ endAdornment: t('partner.marginUnit') }}
            />

            <TextField
              label={t('common.website')}
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              error={Boolean(serverErrors.website)}
              helperText={serverErrors.website}
              inputProps={{ dir: 'auto', maxLength: 400, 'data-testid': 'partner-website' }}
            />

            <TextField
              label={t('common.notes')}
              multiline
              minRows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              inputProps={{ dir: 'auto', 'data-testid': 'partner-notes' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={save.isPending}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={submit} disabled={save.isPending} data-testid="save-partner">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmingDelete}
        title={t('partner.deleteTitle', { name: partner?.name ?? '' })}
        message={t('partner.deleteMessage')}
        busy={remove.isPending}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={async () => {
          if (partner) await remove.mutateAsync(partner.id);
          setConfirmingDelete(false);
          onClose();
        }}
      />
    </>
  );
}
